import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  CalendarX2,
  Car,
  DoorOpen,
  GraduationCap,
  Music4,
  Pencil,
  Repeat,
  RotateCcw,
  Trash2,
} from 'lucide-react'

import { ApiError } from '@/api/client'
import { scheduleApi } from '@/api/endpoints'
import type { CancellationReason, Lesson } from '@/api/types'
import { useAuth } from '@/auth/AuthContext'
import { isTeacher } from '@/auth/permissions'
import { Alert } from '@/components/ui/Alert'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { SelectField, TextField } from '@/components/ui/Field'
import { InstrumentBadge } from '@/components/ui/InstrumentBadge'
import { Modal } from '@/components/ui/Modal'
import { useToast } from '@/components/ui/Toast'
import { formatDayLong, formatTime } from '@/lib/scheduleTime'

const POWODY: { value: CancellationReason; label: string }[] = [
  { value: 'STUDENT', label: 'Odwołane przez ucznia' },
  { value: 'TEACHER', label: 'Odwołane przez nauczyciela' },
  { value: 'SCHOOL', label: 'Odwołane przez szkołę' },
  { value: 'HOLIDAY', label: 'Dzień wolny' },
]

const POWOD_LABEL = Object.fromEntries(POWODY.map((item) => [item.value, item.label]))

/**
 * Co pokazuje okno.
 *
 * Potwierdzenia są krokami tego samego okna, a nie osobnymi oknami nad nim:
 * pytanie „czy na pewno” ma stać tam, gdzie widać, czego dotyczy.
 */
type Tryb = 'podglad' | 'odwolanie' | 'usuniecie' | 'cykl-odwolanie' | 'cykl-usuniecie'

/** Zakres usunięcia cyklu — różnica jest w tym, co dzieje się z historią. */
type ZakresUsuniecia = 'przyszle' | 'wszystko'

interface Props {
  lesson: Lesson | null
  onClose: () => void
  onEdit: (lesson: Lesson) => void
  /** Otwiera formularz całego planu tygodniowego zamiast pojedynczych zajęć. */
  onEditSeries: (seriesId: string) => void
}

export function LessonDetailModal({ lesson, onClose, onEdit, onEditSeries }: Props) {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { notifySuccess } = useToast()

  const [tryb, setTryb] = useState<Tryb>('podglad')
  const [reason, setReason] = useState<CancellationReason>('STUDENT')
  const [note, setNote] = useState('')
  const [zakres, setZakres] = useState<ZakresUsuniecia>('przyszle')
  const [error, setError] = useState<string | null>(null)

  const canManage = !isTeacher(user) || lesson?.teacher.id === user?.id
  const seriesId = lesson?.series_id ?? null

  /** Każda operacja kończy się tak samo: odśwież grafik, powiedz co się stało, zamknij. */
  const zakonczone = (komunikat: string) => async () => {
    await queryClient.invalidateQueries({ queryKey: ['schedule'] })
    notifySuccess(komunikat)
    close()
  }

  const nieudane = (domyslny: string) => (caught: unknown) => {
    setError(caught instanceof ApiError ? caught.message : domyslny)
  }

  const cancel = useMutation({
    mutationFn: () => scheduleApi.cancelLesson(lesson!.id, reason, note.trim() || undefined),
    onSuccess: zakonczone('Zajęcia zostały odwołane.'),
    onError: nieudane('Nie udało się odwołać zajęć.'),
  })

  const restore = useMutation({
    mutationFn: () => scheduleApi.restoreLesson(lesson!.id),
    onSuccess: zakonczone('Zajęcia zostały przywrócone.'),
    onError: nieudane('Nie udało się przywrócić zajęć.'),
  })

  const usun = useMutation({
    mutationFn: () => scheduleApi.deleteLesson(lesson!.id),
    onSuccess: zakonczone('Zajęcia zostały usunięte z grafiku.'),
    onError: nieudane('Nie udało się usunąć zajęć.'),
  })

  const odwolajCykl = useMutation({
    mutationFn: () => scheduleApi.cancelSeries(seriesId!, reason, note.trim() || undefined),
    onSuccess: async (wynik) => {
      await queryClient.invalidateQueries({ queryKey: ['schedule'] })
      notifySuccess(`Odwołano ${wynik.cancelled_count} przyszłych terminów.`)
      close()
    },
    onError: nieudane('Nie udało się odwołać cyklu.'),
  })

  const usunCykl = useMutation({
    mutationFn: async () => {
      if (zakres === 'wszystko') return scheduleApi.purgeSeries(seriesId!)
      await scheduleApi.endSeries(seriesId!)
      return null
    },
    onSuccess: async (wynik) => {
      await queryClient.invalidateQueries({ queryKey: ['schedule'] })
      if (wynik === null) {
        notifySuccess('Cykl zakończony — przyszłe terminy zniknęły z grafiku.')
      } else if (wynik.kept_count > 0) {
        // Zajęcia z obecnością zostają: to podstawa rozliczeń. Człowiek ma
        // o tym wiedzieć od razu, a nie odkryć to później w grafiku.
        notifySuccess(
          `Usunięto ${wynik.removed_count} terminów. ${wynik.kept_count} zostało — mają wpisaną obecność.`,
        )
      } else {
        notifySuccess(`Cykl usunięty razem z ${wynik.removed_count} terminami.`)
      }
      close()
    },
    onError: nieudane('Nie udało się usunąć cyklu.'),
  })

  function close() {
    setTryb('podglad')
    setNote('')
    setZakres('przyszle')
    setError(null)
    onClose()
  }

  if (!lesson) return null

  const start = new Date(lesson.starts_at)
  const isCancelled = lesson.status === 'CANCELLED'
  // Wpisana obecność znaczy, że zajęcia się odbyły — wtedy zostaje odwołanie
  // albo cofnięcie wpisu, bo usunięcie skasowałoby razem z nimi ten wpis.
  const odbyte = lesson.attendance !== null

  const wroc = (
    <Button variant="ghost" onClick={() => setTryb('podglad')}>
      Wróć
    </Button>
  )

  return (
    <Modal
      open
      onClose={close}
      title={lesson.display_title}
      description={`${formatDayLong(start)}, ${formatTime(lesson.starts_at)}–${formatTime(lesson.ends_at)}`}
      footer={
        tryb === 'usuniecie' ? (
          <>
            {wroc}
            <Button variant="danger" isLoading={usun.isPending} onClick={() => usun.mutate()}>
              Usuń z grafiku
            </Button>
          </>
        ) : tryb === 'odwolanie' ? (
          <>
            {wroc}
            <Button variant="danger" isLoading={cancel.isPending} onClick={() => cancel.mutate()}>
              Odwołaj zajęcia
            </Button>
          </>
        ) : tryb === 'cykl-odwolanie' ? (
          <>
            {wroc}
            <Button
              variant="danger"
              isLoading={odwolajCykl.isPending}
              onClick={() => odwolajCykl.mutate()}
            >
              Odwołaj przyszłe terminy
            </Button>
          </>
        ) : tryb === 'cykl-usuniecie' ? (
          <>
            {wroc}
            <Button
              variant="danger"
              isLoading={usunCykl.isPending}
              onClick={() => usunCykl.mutate()}
            >
              {zakres === 'wszystko' ? 'Usuń cały cykl' : 'Usuń przyszłe terminy'}
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={close}>
              Zamknij
            </Button>
            {canManage && isCancelled && (
              <Button
                variant="outline"
                icon={<RotateCcw size={16} />}
                isLoading={restore.isPending}
                onClick={() => restore.mutate()}
              >
                Przywróć
              </Button>
            )}
            {canManage && !odbyte && (
              <Button
                variant="ghost"
                icon={<Trash2 size={16} />}
                onClick={() => setTryb('usuniecie')}
                className="text-error hover:bg-error-container hover:text-error"
              >
                Usuń
              </Button>
            )}
            {canManage && !isCancelled && (
              <>
                <Button
                  variant="outline"
                  icon={<CalendarX2 size={16} />}
                  onClick={() => setTryb('odwolanie')}
                >
                  Odwołaj
                </Button>
                <Button icon={<Pencil size={16} />} onClick={() => onEdit(lesson)}>
                  Edytuj
                </Button>
              </>
            )}
          </>
        )
      }
    >
      <div className="space-y-5">
        {error && <Alert tone="error">{error}</Alert>}

        <div className="flex flex-wrap items-center gap-2">
          {isCancelled ? (
            <Badge tone="error">Odwołane</Badge>
          ) : lesson.status === 'COMPLETED' ? (
            <Badge tone="success">Odbyte</Badge>
          ) : (
            <Badge tone="primary">Zaplanowane</Badge>
          )}
          {seriesId && <Badge tone="neutral">Z planu tygodniowego</Badge>}
          {/* Skąd się tu wzięły te zajęcia — bez tego wyglądają na dodatkowe. */}
          {lesson.is_makeup && <Badge tone="primary">Odrabianie</Badge>}
          {/* Brak instrumentu zostawiałby w rzędzie plakietek sierocy myślnik. */}
          {lesson.instrument && <InstrumentBadge instrument={lesson.instrument} />}
        </div>

        {isCancelled && lesson.cancellation_reason && (
          <Alert tone="warning" title={POWOD_LABEL[lesson.cancellation_reason]}>
            {lesson.cancellation_note ?? 'Nie podano dodatkowego opisu.'}
          </Alert>
        )}

        {!lesson.is_own && (
          <Alert tone="info">
            To zajęcia innego nauczyciela. Widzisz zajętość sali i osobę prowadzącą — dane ucznia są
            zastrzeżone.
          </Alert>
        )}

        {tryb === 'usuniecie' ? (
          <Alert tone="warning" title="Usunąć te zajęcia z grafiku?">
            Znikną bez śladu w historii — to opcja na wpis zrobiony przez pomyłkę. Jeśli zajęcia
            były zaplanowane, ale się nie odbędą, użyj odwołania: zostaje wtedy powód i informacja
            dla rozliczeń.
          </Alert>
        ) : tryb === 'cykl-usuniecie' ? (
          <div className="space-y-4">
            <Alert tone="warning" title="Co zrobić z tym planem tygodniowym?">
              Zajęcia z wpisaną obecnością zostają w obu przypadkach — są podstawą rozliczenia z
              nauczycielem i rodzicem.
            </Alert>
            <div className="space-y-3">
              <Wybor
                checked={zakres === 'przyszle'}
                onSelect={() => setZakres('przyszle')}
                title="Usuń przyszłe terminy"
                description="Plan się kończy, historia zostaje nietknięta. To zwykły koniec współpracy albo semestru."
              />
              <Wybor
                checked={zakres === 'wszystko'}
                onSelect={() => setZakres('wszystko')}
                title="Usuń cały cykl razem z historią"
                description="Także terminy z przeszłości. Do planu wpisanego przez pomyłkę, którego nigdy nie było."
              />
            </div>
          </div>
        ) : tryb === 'odwolanie' || tryb === 'cykl-odwolanie' ? (
          <div className="space-y-4">
            <Alert tone="info">
              {tryb === 'cykl-odwolanie'
                ? 'Odwołane zostaną wszystkie przyszłe terminy tego planu. Zostaną w historii wraz z powodem i zwolnią salę — pojedynczy termin da się później przywrócić.'
                : 'Odwołane zajęcia znikną z siatki, ale zostaną w historii i zwolnią salę.'}
            </Alert>
            <SelectField
              label="Powód odwołania"
              value={reason}
              onChange={(event) => setReason(event.target.value as CancellationReason)}
              options={POWODY}
              hint="Powód wpływa na rozliczenia — odwołanie przez ucznia i przez szkołę liczą się inaczej."
              required
            />
            <TextField
              label="Dodatkowy opis"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="np. choroba, wyjazd na konkurs"
              maxLength={255}
            />
          </div>
        ) : (
          <>
            <dl className="grid gap-4 sm:grid-cols-2">
              {lesson.room ? (
                <Detail icon={<DoorOpen size={16} />} label="Sala" value={lesson.room.name} />
              ) : (
                <Detail
                  icon={<Car size={16} />}
                  label="Miejsce"
                  value="Zajęcia z dojazdem — u ucznia"
                />
              )}
              <Detail
                icon={<GraduationCap size={16} />}
                label="Prowadzący"
                value={lesson.teacher.full_name}
              />
              {lesson.is_own && (
                <Detail
                  icon={<Music4 size={16} />}
                  label="Uczeń"
                  value={lesson.student?.full_name ?? 'Zajęcia grupowe'}
                  to={lesson.student ? `/uczniowie/${lesson.student.id}` : undefined}
                />
              )}
              <Detail label="Czas trwania" value={`${lesson.duration_minutes} minut`} />
              <Detail label="Oddział" value={lesson.location.name} />
            </dl>

            {/* Operacje na całym planie stoją osobno od tych na pojedynczych
                zajęciach. Wcześniej dało się zmienić wyłącznie ten jeden termin,
                więc przesunięcie stałych zajęć znaczyło poprawianie ich po kolei
                albo zakładanie planu od nowa. */}
            {seriesId && canManage && (
              <div className="rounded-lg border border-outline/20 p-4">
                <p className="flex items-center gap-2 text-sm font-bold text-on-surface">
                  <Repeat size={16} aria-hidden="true" />
                  Cały plan tygodniowy
                </p>
                <p className="mt-1 text-xs text-on-surface-variant">
                  Te działania obejmą przyszłe terminy planu. Przyciski na dole okna dotyczą
                  wyłącznie tych zajęć.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    icon={<Pencil size={14} />}
                    onClick={() => onEditSeries(seriesId)}
                  >
                    Edytuj cykl
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    icon={<CalendarX2 size={14} />}
                    onClick={() => setTryb('cykl-odwolanie')}
                  >
                    Odwołaj cykl
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Trash2 size={14} />}
                    onClick={() => setTryb('cykl-usuniecie')}
                    className="text-error hover:bg-error-container hover:text-error"
                  >
                    Usuń cykl
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {tryb === 'podglad' && lesson.notes && (
          <div className="rounded-lg bg-surface-container-low p-4">
            <p className="text-xs font-bold uppercase tracking-wide text-on-surface-variant">
              Notatka
            </p>
            <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed">{lesson.notes}</p>
          </div>
        )}
      </div>
    </Modal>
  )
}

/** Jedna z dwóch dróg usunięcia cyklu — różnicę widać obok, nie po kliknięciu. */
function Wybor({
  checked,
  onSelect,
  title,
  description,
}: {
  checked: boolean
  onSelect: () => void
  title: string
  description: string
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-lg border-2 border-outline/20 p-3 transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
      <input
        type="radio"
        name="zakres-usuniecia"
        checked={checked}
        onChange={onSelect}
        className="mt-1 h-4 w-4 border-outline/40 text-primary focus:ring-primary"
      />
      <span>
        <span className="block text-sm font-bold text-on-surface">{title}</span>
        <span className="block text-xs text-on-surface-variant">{description}</span>
      </span>
    </label>
  )
}

function Detail({
  icon,
  label,
  value,
  to,
}: {
  icon?: React.ReactNode
  label: string
  value: string
  to?: string
}) {
  return (
    <div>
      <dt className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-on-surface-variant">
        {icon && <span aria-hidden="true">{icon}</span>}
        {label}
      </dt>
      <dd className="mt-1 text-sm font-semibold text-on-surface">
        {to ? (
          <Link to={to} className="text-primary underline-offset-2 hover:underline">
            {value}
          </Link>
        ) : (
          value
        )}
      </dd>
    </div>
  )
}
